// biome-ignore-all lint/a11y: TODO - address a11y issues later
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type {
  Header,
  Table as ReactTable,
  SortDirection,
} from "@tanstack/react-table";
import { Calculator, Filter } from "lucide-react";
import type { MouseEvent, MouseEventHandler } from "react";
import React from "react";
import { ColumnActionsMenu } from "@/components/column-actions-menu";
import type { FilterValue } from "@/components/filter-popover";
import { FilterPopover } from "@/components/filter-popover";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { TableHead } from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { getColumnDisplayTitle, hasColumnAlias } from "@/lib/column-aliases";
import { getColumnCapabilities } from "@/lib/column-capabilities-constants";
import { isFilterValueActive } from "@/lib/filter-value-utils";
import { cn } from "@/lib/utils";
import type { UnifiedTableColumn } from "@/types/table-column";

export interface DraggableTableHeadProps<TData, TValue> {
  header: Header<TData, TValue>;
  table: ReactTable<TData>;
  handleSort: (colId: string) => void;
  sortConfig: { key: string; direction: SortDirection | null };
  renderSortIcon: (column: UnifiedTableColumn<TData>) => React.ReactNode;
  filters: Record<string | keyof TData, unknown>;
  updateFilter: (colId: string, filter: FilterValue) => void;
  clearFilter: (colId: string) => void;
  columns: UnifiedTableColumn<TData>[];
  columnTitle: React.ReactNode;
  // Optional props
  handleDoubleClickResize?: (e: MouseEvent, colId: string) => void;
  isMetricLimitExceeded?: boolean;
  getMetricLimitTooltipText?: () => string;
  // Pin functionality
  isPinned?: boolean;
  onTogglePin?: (columnId: string) => void;
  pinnedLeft?: number;
  // Resize functionality
  onResizeToFit?: (columnId: string) => void;
  // Remove column functionality
  onRemoveColumn?: (columnId: string) => void;
  // Alias functionality
  columnAliases?: Record<string, string>;
  onSetAlias?: (columnId: string) => void;
  // Wrap functionality
  isWrapped?: boolean;
  onToggleWrap?: (columnId: string) => void;
  // Read-only state
  isLocked?: boolean;
}

export function DraggableTableHead<TData, TValue>({
  header,
  table,
  handleSort,
  sortConfig,
  renderSortIcon,
  filters,
  updateFilter,
  clearFilter,
  columns,
  columnTitle,
  handleDoubleClickResize,
  isMetricLimitExceeded,
  getMetricLimitTooltipText,
  isPinned,
  onTogglePin,
  pinnedLeft,
  onResizeToFit,
  onRemoveColumn,
  columnAliases,
  onSetAlias,
  isWrapped = false,
  onToggleWrap,
  isLocked = false,
}: DraggableTableHeadProps<TData, TValue>) {
  const [isTitleTruncated, setIsTitleTruncated] = React.useState(false);
  const [filterPopoverOpen, setFilterPopoverOpen] = React.useState(false);
  const titleRef = React.useRef<HTMLSpanElement | null>(null);

  const column = header.column.columnDef;
  const categoryStyle = column.meta?.categoryStyle;
  const colId = column.id!;
  const unifiedColumn = columns.find((col) => col.key.toString() === colId);
  const meta =
    (column.meta as {
      title?: string;
      sortable?: boolean;
      resizable?: boolean;
      headStyle?: string;
    }) || {};
  // Tooltip text for the native browser tooltip
  const headTitle =
    meta.title ?? (typeof columnTitle === "string" ? columnTitle : undefined);

  // Get column capabilities with defaults applied
  const capabilities = getColumnCapabilities(meta);
  const canSort = capabilities.sortable;
  // Check columnType from metadata OR column key prefix for metric/formula columns
  const isMetricColumn =
    unifiedColumn?.columnType === "metric" || colId.startsWith("metric_");
  const isFormulaColumn =
    unifiedColumn?.columnType === "formula" || colId.startsWith("formula_");
  const renderedSortIcon = unifiedColumn ? renderSortIcon(unifiedColumn) : null;
  const filterIsActive = isFilterValueActive(filters[colId] as FilterValue);

  // Handle column aliases - use alias for display if available
  const aliases = columnAliases || {};
  const hasAlias = hasColumnAlias(colId, aliases);
  const displayTitle = getColumnDisplayTitle(
    colId,
    typeof columnTitle === "string" ? columnTitle : headTitle || "",
    aliases,
  );

  // Detect if title is truncated
  // biome-ignore lint/correctness/useExhaustiveDependencies: displayTitle needed to re-check truncation when column alias changes
  React.useEffect(() => {
    if (!titleRef.current) return;

    const checkTruncation = () => {
      // Add null check here to prevent TypeError
      if (!titleRef.current) return;

      const el = titleRef.current;
      // Check if text is truncated either horizontally or vertically (clamped at 2 lines)
      const isHorizontallyTruncated = el.scrollWidth > el.clientWidth + 1;
      const isVerticallyClamped = el.scrollHeight > el.clientHeight + 1;
      setIsTitleTruncated(isHorizontallyTruncated || isVerticallyClamped);
    };

    checkTruncation();

    const resizeObserver = new ResizeObserver(checkTruncation);
    resizeObserver.observe(titleRef.current);
    return () => resizeObserver.disconnect();
  }, [displayTitle]);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: colId,
  });

  if (transform) {
    transform.scaleX = 1;
    transform.scaleY = 1;
  }

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    // For pinned columns, ensure proper sticky positioning that accounts for scroll
    ...(isPinned &&
      pinnedLeft !== undefined && {
        left: `${pinnedLeft}px`,
        // Ensure z-index is set for proper layering during scroll
        zIndex: isPinned ? 20 : undefined,
      }),
  };

  const renderHeaderContent = () => {
    // Title element (flex item that can shrink)
    const titleEl = (
      <span
        key="title"
        ref={titleRef}
        className={cn(
          "flex-1 min-w-0 transition-all", // animate padding smoothly
          "line-clamp-2", // Allow up to 2 lines with ellipsis
          isTitleTruncated && "group-hover:pr-4", // Only add padding if title is truncated
        )}
      >
        {displayTitle}
      </span>
    );

    // Formula (calculator) icon
    const calcIcon = (
      <Calculator
        key="calc"
        className="h-3 w-3 text-muted-foreground flex-shrink-0 ml-1"
      />
    );

    // Filter icon (interactive when not locked)
    const filterIcon = (
      <Popover
        key="filter"
        modal
        open={!isLocked && filterPopoverOpen}
        onOpenChange={(open) => !isLocked && setFilterPopoverOpen(open)}
      >
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              "h-3 w-3 p-0 ml-1",
              isLocked
                ? "text-muted-foreground/50 cursor-not-allowed"
                : "text-green-600 hover:text-green-700",
            )}
            disabled={isLocked}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              if (isLocked) return;
            }}
          >
            <Filter className="h-3 w-3" />
          </Button>
        </PopoverTrigger>
        {!isLocked && (
          <PopoverContent
            side="bottom"
            sideOffset={8}
            align="end"
            className="p-0 border-none bg-transparent shadow-none"
          >
            <FilterPopover
              standalone={false}
              column={
                (unifiedColumn || {
                  key: colId,
                  title: String(columnTitle),
                  sortable: false,
                  filterable: false,
                }) as UnifiedTableColumn
              }
              currentFilter={filters[colId] as FilterValue}
              onFilterChange={updateFilter}
              onClearFilter={clearFilter}
              onRequestClose={() => setFilterPopoverOpen(false)}
            />
          </PopoverContent>
        )}
      </Popover>
    );

    // Sort icon (non-interactive)
    const sortIcon = (
      <span key="sort" className="ml-1 pointer-events-none">
        {renderedSortIcon}
      </span>
    );

    const iconEls: React.ReactElement[] = [];
    if (isFormulaColumn) {
      iconEls.push(calcIcon);
    }
    if (filterIsActive) {
      iconEls.push(filterIcon);
    }
    if (sortConfig.key === colId && renderedSortIcon) {
      iconEls.push(sortIcon);
    }

    const iconsWrapper = (
      <span
        key="icons"
        className={cn(
          "flex items-center flex-shrink-0 space-x-1 transition-transform duration-150",
          isTitleTruncated && "group-hover:-translate-x-4", // Only slide if title is truncated
        )}
      >
        {iconEls}
      </span>
    );

    const parts: React.ReactNode[] = [];
    parts.push(titleEl);
    parts.push(iconsWrapper);

    const content = (
      <div className="flex items-center w-full text-xs overflow-hidden">
        {parts}
      </div>
    );

    // Handle metric limit exceeded tooltip (highest priority)
    if (
      (isMetricColumn || isFormulaColumn) &&
      isMetricLimitExceeded &&
      getMetricLimitTooltipText
    ) {
      return (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>{content}</TooltipTrigger>
          <TooltipContent className="max-w-[20rem]">
            <p>{getMetricLimitTooltipText()}</p>
          </TooltipContent>
        </Tooltip>
      );
    }

    // Handle custom tooltip for metric columns (when not limit exceeded)
    if (isMetricColumn && unifiedColumn?.tooltip) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>{content}</TooltipTrigger>
          <TooltipContent className="max-w-[20rem]">
            <div className="space-y-1">
              <p className="font-bold">{unifiedColumn.title}</p>
              <p>{unifiedColumn.tooltip}</p>
            </div>
          </TooltipContent>
        </Tooltip>
      );
    }

    return content;
  };

  return (
    <TableHead
      ref={setNodeRef}
      key={header.id}
      data-column-id={colId}
      data-column-order={header.column.getIndex()}
      data-testid={`column-header-${colId}`}
      style={{ ...style, width: header.getSize() }}
      colSpan={header.colSpan}
      title={
        // Only use native title for non-metric columns or metric columns without custom tooltips
        isMetricColumn && unifiedColumn?.tooltip ? undefined : headTitle
      }
      className={cn(
        "relative group border-b border-r border-border",
        categoryStyle,
        meta?.headStyle,
        header.column.getIsResizing() && "table-head-resizing",
        isPinned && "pinned-column",
        canSort && "cursor-pointer",
      )}
      onClick={
        canSort &&
        !((isMetricColumn || isFormulaColumn) && isMetricLimitExceeded)
          ? () => handleSort(colId)
          : undefined
      }
      {...attributes}
      {...listeners}
    >
      {header.isPlaceholder ? null : (
        <div className="flex items-center justify-between">
          <div className="overflow-hidden">
            {canSort ? (
              <div
                className="flex items-center w-full overflow-hidden"
                onKeyUp={(e) => e.stopPropagation()}
              >
                {renderHeaderContent()}
              </div>
            ) : (
              <span className="text-xs line-clamp-2 block overflow-hidden">
                {displayTitle}
              </span>
            )}
          </div>

          {/* Kebab menu with pin + filter (absolutely positioned, no layout impact) */}
          <div
            className="absolute right-0 top-0 h-full flex items-center"
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onMouseMove={(e) => e.stopPropagation()}
            onDragStart={(e) => e.stopPropagation()}
            onDrag={(e) => e.stopPropagation()}
          >
            <ColumnActionsMenu
              column={
                (unifiedColumn || {
                  key: colId,
                  title: String(columnTitle),
                  sortable: false,
                  filterable: false,
                  required: false,
                }) as UnifiedTableColumn
              }
              isPinned={!!isPinned}
              isWrapped={!!isWrapped}
              currentFilter={filters[colId] as FilterValue}
              updateFilter={updateFilter}
              clearFilter={clearFilter}
              onTogglePin={onTogglePin}
              onToggleWrap={onToggleWrap}
              onResizeToFit={onResizeToFit}
              onRemoveColumn={onRemoveColumn}
              hasAlias={hasAlias}
              onSetAlias={onSetAlias}
            />
          </div>
        </div>
      )}

      {/* Resize Handle - only show for resizable columns */}
      {header.column.getCanResize() && (
        <div
          onMouseDown={header.getResizeHandler()}
          onTouchStart={header.getResizeHandler()}
          onClick={(e) => e.stopPropagation()}
          onDoubleClick={
            ((e) => {
              e.stopPropagation();
              handleDoubleClickResize?.(e, header.column.id);
            }) satisfies MouseEventHandler<HTMLDivElement>
          }
          className={cn(
            "resize-handle",
            header.column.getIsResizing() && "table-head-resizing",
          )}
          data-resize-handle={header.id}
          data-near-bottom={
            header.column.getIndex() > table.getAllColumns().length * 0.75
              ? "true"
              : undefined
          }
          title="Double-click to auto-size"
        />
      )}
    </TableHead>
  );
}
